/**
 * Per-event attribute and entity-context schemas, generated by
 * `scripts/generate-event-catalog.ts` from
 * the vendored workflow event catalog artifact (ENG-11425): `eventCatalog.json`,
 * document version 2.
 *
 * Generated file — regenerate it with `pnpm run event-catalog:generate` rather than
 * editing it. `pnpm run event-catalog:fetch` refreshes the artifact it reads.
 *
 * The backend owns everything here: an attribute's canonical snake_case key, the
 * field type it filters as, and — for an enum — its member list in the order the
 * backend declares them, which is meaningful for these closed sets (a severity scale,
 * the two edges of an alarm). Attributes are ordered as the artifact sorts them, by
 * name.
 *
 * What the builder makes of an attribute — whether it is offered, whether ingestion
 * populates it, its display unit, its label — is authored in `./catalog` against
 * these keys. `object`-typed attributes are nested payloads no operator or template
 * can address, so the generator leaves them out (see its own header).
 *
 * `EVENT_CONTEXT` is the second half: the entities the executor resolves from a
 * triggering event of each type, each with the fields it carries and whether it
 * resolves on every event (`always`) or only when the event supplies the key it
 * hangs off (`conditional` — absent values arrive as null). Entities and their
 * fields are ordered as the artifact sorts them, by name. An event with no reachable
 * entity maps to `{}`.
 *
 * `as const satisfies Record<EventType, …>` on the way out: the literal keys are what
 * `AttributeKeyFor` and `ContextFieldKeyFor` expose to consumers, and the `Record`
 * makes an event type the artifact describes but the generated API client does not —
 * or the reverse — a compile error rather than a silent gap.
 */
import type { EventType } from '~/data/api/client/workflows'

import type { CatalogAttribute, CatalogContextEntities } from './catalog'

export const EVENT_ATTRIBUTES = {
  'charger.boot_notification': [
    { key: 'charge_box_serial_number', type: 'string' },
    { key: 'charge_point_model', type: 'string' },
    { key: 'charge_point_serial_number', type: 'string' },
    { key: 'charge_point_vendor', type: 'string' },
    { key: 'charger_id', type: 'string' },
    { key: 'charger_name', type: 'string' },
    { key: 'firmware_version', type: 'string' },
    { key: 'iccid', type: 'string' },
    { key: 'imsi', type: 'string' },
    { key: 'meter_serial_number', type: 'string' },
    { key: 'meter_type', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'site_id', type: 'string' },
    { key: 'timestamp', type: 'datetime' }
  ],
  'charger.data_transfer': [
    { key: 'charger_id', type: 'string' },
    { key: 'charger_name', type: 'string' },
    { key: 'message_id', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'request_id', type: 'string' },
    { key: 'site_id', type: 'string' },
    { key: 'timestamp', type: 'datetime' },
    { key: 'vendor_id', type: 'string' }
  ],
  'charger.start_transaction': [
    { key: 'charger_id', type: 'string' },
    { key: 'charger_name', type: 'string' },
    { key: 'connector_id', type: 'number' },
    { key: 'id', type: 'string' },
    { key: 'ocpp_tag', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'site_id', type: 'string' },
    { key: 'start_timestamp', type: 'datetime' }
  ],
  'charger.status_notification': [
    { key: 'charger_id', type: 'string' },
    { key: 'charger_name', type: 'string' },
    { key: 'connector_id', type: 'number' },
    { key: 'error_code', type: 'string' },
    { key: 'error_info', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'severity', type: 'enum', enumValues: ['fault', 'warning'] },
    { key: 'site_id', type: 'string' },
    {
      key: 'status',
      type: 'enum',
      enumValues: [
        'Available',
        'Preparing',
        'Charging',
        'Finishing',
        'SuspendedEVSE',
        'SuspendedEV',
        'Reserved',
        'PartiallyFaulted',
        'Faulted',
        'Unavailable',
        'Unknown',
        'Offline'
      ]
    },
    { key: 'timestamp', type: 'datetime' },
    { key: 'vendor_error_code', type: 'string' }
  ],
  'charger.stop_transaction': [
    { key: 'charger_id', type: 'string' },
    { key: 'charger_name', type: 'string' },
    { key: 'connector_id', type: 'number' },
    { key: 'id', type: 'string' },
    { key: 'ocpp_tag', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'site_id', type: 'string' },
    { key: 'stop_event_actor', type: 'string' },
    { key: 'stop_reason', type: 'string' },
    { key: 'stop_timestamp', type: 'datetime' }
  ],
  'charger.transaction_summary': [
    { key: 'charger_id', type: 'string' },
    { key: 'connector_id', type: 'number' },
    { key: 'latest_power_import', type: 'number' },
    { key: 'latest_soc', type: 'number' },
    { key: 'max_power_import', type: 'number' },
    { key: 'max_voltage', type: 'number' },
    { key: 'mean_power_import', type: 'number' },
    {
      key: 'message_type',
      type: 'enum',
      enumValues: ['StartTransaction', 'StopTransaction', 'MeterValuesBatch', 'PlugOutEvent']
    },
    { key: 'min_voltage', type: 'number' },
    { key: 'node_id', type: 'string' },
    { key: 'ocpp_tag', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'plug_in_time', type: 'datetime' },
    { key: 'plug_out_time', type: 'datetime' },
    { key: 'site_id', type: 'string' },
    { key: 'start_timestamp', type: 'datetime' },
    { key: 'starting_soc', type: 'number' },
    { key: 'stop_event_actor', type: 'string' },
    { key: 'stop_reason', type: 'string' },
    { key: 'stop_timestamp', type: 'datetime' },
    { key: 'timestamp', type: 'datetime' },
    { key: 'total_energy_exported', type: 'number' },
    { key: 'total_energy_imported', type: 'number' },
    { key: 'transaction_id', type: 'string' }
  ],
  'condition.opened': [
    { key: 'correlation_key', type: 'string' },
    { key: 'monitor_id', type: 'string' },
    { key: 'opened_at', type: 'datetime' },
    { key: 'workflow_id', type: 'string' },
    { key: 'workflow_version', type: 'number' }
  ],
  'condition.resolved': [
    { key: 'correlation_key', type: 'string' },
    { key: 'monitor_id', type: 'string' },
    { key: 'opened_at', type: 'datetime' },
    { key: 'resolved_at', type: 'datetime' },
    { key: 'workflow_id', type: 'string' },
    { key: 'workflow_version', type: 'number' }
  ],
  'der.fault': [
    { key: 'alarm_code', type: 'string' },
    { key: 'alarm_reason', type: 'string' },
    { key: 'asset_name', type: 'string' },
    {
      key: 'asset_type',
      type: 'enum',
      enumValues: ['BESS', 'GENSET', 'PANEL', 'INVERTER', 'SOLAR_ARRAY', 'FUEL_TANK', 'LOAD', 'GRID_CONNECTION']
    },
    { key: 'der_asset_id', type: 'string' },
    { key: 'location', type: 'string' },
    { key: 'organization_id', type: 'string' },
    { key: 'severity', type: 'enum', enumValues: ['WARNING', 'ERROR', 'CRITICAL'] },
    { key: 'site_id', type: 'string' },
    { key: 'timestamp', type: 'datetime' },
    { key: 'transition', type: 'enum', enumValues: ['RAISED', 'CLEARED'] }
  ],
  'entity.connectivity': [
    { key: 'active', type: 'boolean' },
    { key: 'entity_id', type: 'string' },
    { key: 'entity_type', type: 'string' },
    { key: 'last_heard_at', type: 'datetime' },
    { key: 'organization_id', type: 'string' },
    { key: 'site_id', type: 'string' }
  ],
  scheduled: [
    { key: 'fired_at', type: 'datetime' },
    { key: 'scheduled_time', type: 'datetime' },
    { key: 'trigger_id', type: 'string' },
    { key: 'workflow_id', type: 'string' },
    { key: 'workflow_version', type: 'number' }
  ],
  'site.power_utilization': [
    { key: 'limit_kw', type: 'number' },
    { key: 'organization_id', type: 'string' },
    { key: 'site_id', type: 'string' },
    { key: 'site_name', type: 'string' },
    { key: 'utilization_kw', type: 'number' }
  ],
  'vehicle.fault': [
    { key: 'amber_lamp_on', type: 'boolean' },
    { key: 'dtc_description', type: 'string' },
    { key: 'dtc_id', type: 'string' },
    { key: 'dtc_short_code', type: 'string' },
    { key: 'fmi', type: 'number' },
    { key: 'fmi_text', type: 'string' },
    { key: 'latitude', type: 'number' },
    { key: 'longitude', type: 'number' },
    { key: 'mil_lamp_on', type: 'boolean' },
    { key: 'occurrence_count', type: 'number' },
    { key: 'organization_id', type: 'string' },
    { key: 'protect_lamp_on', type: 'boolean' },
    { key: 'provider', type: 'enum', enumValues: ['Geotab', 'Zonar'] },
    { key: 'red_lamp_on', type: 'boolean' },
    { key: 'source_address', type: 'number' },
    { key: 'source_label', type: 'string' },
    { key: 'source_name', type: 'string' },
    { key: 'spn', type: 'number' },
    { key: 'spn_description', type: 'string' },
    { key: 'spn_name', type: 'string' },
    { key: 'timestamp', type: 'datetime' },
    { key: 'vehicle_id', type: 'string' }
  ],
  'vehicle.telemetry': [
    { key: 'distance_until_home_km', type: 'number' },
    { key: 'fleet_id', type: 'string' },
    { key: 'latitude', type: 'number' },
    { key: 'longitude', type: 'number' },
    { key: 'odometer_km', type: 'number' },
    { key: 'organization_id', type: 'string' },
    { key: 'remaining_range_km', type: 'number' },
    { key: 'site_id', type: 'string' },
    { key: 'source_provider', type: 'string' },
    { key: 'speed_kph', type: 'number' },
    { key: 'state_of_charge', type: 'number' },
    { key: 'time_until_home_minutes', type: 'number' },
    { key: 'timestamp', type: 'datetime' },
    { key: 'vehicle_id', type: 'string' },
    { key: 'vehicle_status', type: 'string' }
  ]
} as const satisfies Record<EventType, readonly CatalogAttribute[]>

export const EVENT_CONTEXT = {
  'charger.boot_notification': {
    charger: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    }
  },
  'charger.data_transfer': {
    charger: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    }
  },
  'charger.start_transaction': {
    charger: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    connector: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'connector_name', type: 'string' },
        { key: 'evse_id', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    },
    transaction: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'latest_soc', type: 'number' },
        { key: 'max_power_import', type: 'number' },
        { key: 'mean_power_import', type: 'number' },
        { key: 'ocpp_tag', type: 'string' },
        { key: 'plug_in_time', type: 'datetime' },
        { key: 'plug_out_time', type: 'datetime' },
        { key: 'site_id', type: 'string' },
        { key: 'start_timestamp', type: 'datetime' },
        { key: 'starting_soc', type: 'number' },
        { key: 'stop_timestamp', type: 'datetime' },
        { key: 'total_energy_exported', type: 'number' },
        { key: 'total_energy_imported', type: 'number' },
        { key: 'transaction_id', type: 'string' },
        { key: 'transaction_pk', type: 'number' },
        { key: 'vehicle_id', type: 'string' }
      ]
    },
    vehicle: {
      resolution: 'conditional',
      fields: [
        { key: 'home_site_id', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'odometer', type: 'number' },
        { key: 'organization_id', type: 'string' },
        { key: 'remaining_range', type: 'number' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'vehicle_id', type: 'string' },
        { key: 'vehicle_name', type: 'string' },
        { key: 'vehicle_status', type: 'string' }
      ]
    }
  },
  'charger.status_notification': {
    charger: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    connector: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'connector_name', type: 'string' },
        { key: 'evse_id', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    },
    transaction: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'latest_soc', type: 'number' },
        { key: 'max_power_import', type: 'number' },
        { key: 'mean_power_import', type: 'number' },
        { key: 'ocpp_tag', type: 'string' },
        { key: 'plug_in_time', type: 'datetime' },
        { key: 'plug_out_time', type: 'datetime' },
        { key: 'site_id', type: 'string' },
        { key: 'start_timestamp', type: 'datetime' },
        { key: 'starting_soc', type: 'number' },
        { key: 'stop_timestamp', type: 'datetime' },
        { key: 'total_energy_exported', type: 'number' },
        { key: 'total_energy_imported', type: 'number' },
        { key: 'transaction_id', type: 'string' },
        { key: 'transaction_pk', type: 'number' },
        { key: 'vehicle_id', type: 'string' }
      ]
    },
    vehicle: {
      resolution: 'conditional',
      fields: [
        { key: 'home_site_id', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'odometer', type: 'number' },
        { key: 'organization_id', type: 'string' },
        { key: 'remaining_range', type: 'number' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'vehicle_id', type: 'string' },
        { key: 'vehicle_name', type: 'string' },
        { key: 'vehicle_status', type: 'string' }
      ]
    }
  },
  'charger.stop_transaction': {
    charger: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    connector: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'connector_name', type: 'string' },
        { key: 'evse_id', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    },
    transaction: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'latest_soc', type: 'number' },
        { key: 'max_power_import', type: 'number' },
        { key: 'mean_power_import', type: 'number' },
        { key: 'ocpp_tag', type: 'string' },
        { key: 'plug_in_time', type: 'datetime' },
        { key: 'plug_out_time', type: 'datetime' },
        { key: 'site_id', type: 'string' },
        { key: 'start_timestamp', type: 'datetime' },
        { key: 'starting_soc', type: 'number' },
        { key: 'stop_timestamp', type: 'datetime' },
        { key: 'total_energy_exported', type: 'number' },
        { key: 'total_energy_imported', type: 'number' },
        { key: 'transaction_id', type: 'string' },
        { key: 'transaction_pk', type: 'number' },
        { key: 'vehicle_id', type: 'string' }
      ]
    },
    vehicle: {
      resolution: 'conditional',
      fields: [
        { key: 'home_site_id', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'odometer', type: 'number' },
        { key: 'organization_id', type: 'string' },
        { key: 'remaining_range', type: 'number' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'vehicle_id', type: 'string' },
        { key: 'vehicle_name', type: 'string' },
        { key: 'vehicle_status', type: 'string' }
      ]
    }
  },
  'charger.transaction_summary': {
    charger: {
      resolution: 'always',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    connector: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'connector_name', type: 'string' },
        { key: 'evse_id', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    },
    transaction: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'connector_id', type: 'number' },
        { key: 'latest_soc', type: 'number' },
        { key: 'max_power_import', type: 'number' },
        { key: 'mean_power_import', type: 'number' },
        { key: 'ocpp_tag', type: 'string' },
        { key: 'plug_in_time', type: 'datetime' },
        { key: 'plug_out_time', type: 'datetime' },
        { key: 'site_id', type: 'string' },
        { key: 'start_timestamp', type: 'datetime' },
        { key: 'starting_soc', type: 'number' },
        { key: 'stop_timestamp', type: 'datetime' },
        { key: 'total_energy_exported', type: 'number' },
        { key: 'total_energy_imported', type: 'number' },
        { key: 'transaction_id', type: 'string' },
        { key: 'transaction_pk', type: 'number' },
        { key: 'vehicle_id', type: 'string' }
      ]
    },
    vehicle: {
      resolution: 'conditional',
      fields: [
        { key: 'home_site_id', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'odometer', type: 'number' },
        { key: 'organization_id', type: 'string' },
        { key: 'remaining_range', type: 'number' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'vehicle_id', type: 'string' },
        { key: 'vehicle_name', type: 'string' },
        { key: 'vehicle_status', type: 'string' }
      ]
    }
  },
  'condition.opened': {},
  'condition.resolved': {},
  'der.fault': {
    bess: {
      resolution: 'conditional',
      fields: [
        { key: 'available_charge_energy', type: 'number' },
        { key: 'available_discharge_energy', type: 'number' },
        { key: 'bess_id', type: 'string' },
        { key: 'bess_name', type: 'string' },
        { key: 'charge_power_limit', type: 'number' },
        { key: 'control_mode', type: 'string' },
        { key: 'discharge_power_limit', type: 'number' },
        { key: 'energy_capacity', type: 'number' },
        { key: 'firmware_version', type: 'string' },
        { key: 'grid_mode', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'manufacturer', type: 'string' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'run_state', type: 'enum', enumValues: ['NORMAL', 'DEGRADED', 'CRITICAL'] },
        { key: 'site_id', type: 'string' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'state_of_health', type: 'number' }
      ]
    },
    generator: {
      resolution: 'conditional',
      fields: [
        { key: 'control_mode', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'fuel_type', type: 'enum', enumValues: ['DIESEL', 'GASOLINE', 'LP_VAPOR', 'NATURAL_GAS'] },
        { key: 'generator_id', type: 'string' },
        { key: 'generator_name', type: 'string' },
        { key: 'grid_mode', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'manufacturer', type: 'string' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'power_output', type: 'number' },
        { key: 'rated_apparent_power', type: 'number' },
        { key: 'rated_power', type: 'number' },
        { key: 'run_state', type: 'enum', enumValues: ['NORMAL', 'DEGRADED', 'CRITICAL'] },
        { key: 'site_id', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    }
  },
  'entity.connectivity': {
    charger: {
      resolution: 'conditional',
      fields: [
        { key: 'charger_id', type: 'string' },
        { key: 'charger_name', type: 'string' },
        { key: 'connectivity_status', type: 'string' },
        { key: 'firmware_version', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'max_power', type: 'number' },
        { key: 'model', type: 'string' },
        { key: 'operational_status', type: 'string' },
        { key: 'site_id', type: 'string' },
        { key: 'status', type: 'string' },
        { key: 'vendor', type: 'string' }
      ]
    },
    link: {
      resolution: 'conditional',
      fields: [
        { key: 'connectivity_status', type: 'enum', enumValues: ['Online', 'Offline'] },
        { key: 'description', type: 'string' },
        { key: 'last_heard_at', type: 'datetime' },
        { key: 'link_id', type: 'string' },
        { key: 'name', type: 'string' },
        { key: 'nickname', type: 'string' },
        { key: 'site_id', type: 'string' }
      ]
    },
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    }
  },
  scheduled: {},
  'site.power_utilization': {
    site: {
      resolution: 'always',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    }
  },
  'vehicle.fault': {
    vehicle: {
      resolution: 'always',
      fields: [
        { key: 'home_site_id', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'odometer', type: 'number' },
        { key: 'organization_id', type: 'string' },
        { key: 'remaining_range', type: 'number' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'vehicle_id', type: 'string' },
        { key: 'vehicle_name', type: 'string' },
        { key: 'vehicle_status', type: 'string' }
      ]
    }
  },
  'vehicle.telemetry': {
    site: {
      resolution: 'conditional',
      fields: [
        { key: 'address', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'num_chargers', type: 'number' },
        { key: 'num_vehicles', type: 'number' },
        { key: 'power_ceiling', type: 'number' },
        { key: 'site_id', type: 'string' },
        { key: 'site_name', type: 'string' },
        { key: 'timezone', type: 'string' }
      ]
    },
    vehicle: {
      resolution: 'always',
      fields: [
        { key: 'home_site_id', type: 'string' },
        { key: 'latitude', type: 'number' },
        { key: 'longitude', type: 'number' },
        { key: 'odometer', type: 'number' },
        { key: 'organization_id', type: 'string' },
        { key: 'remaining_range', type: 'number' },
        { key: 'state_of_charge', type: 'number' },
        { key: 'vehicle_id', type: 'string' },
        { key: 'vehicle_name', type: 'string' },
        { key: 'vehicle_status', type: 'string' }
      ]
    }
  }
} as const satisfies Record<EventType, CatalogContextEntities>
